import { TeamGameResponse } from '@dtov1/dtos';
import { DateTime } from 'luxon';

export const formatScheduleCardDates = (ts: number, tz?: string) => {
  const dateTimeRaw = DateTime.fromSeconds(ts, {
    zone: tz,
  });
  const dateTimeLocal = dateTimeRaw.setZone('local');
  const date = dateTimeLocal.toFormat('MMM dd');
  const time = dateTimeLocal.toFormat('t');
  return { date, time };
};

export const formatTsToDayYearDate = (ts: number): string => {
  return DateTime.fromSeconds(ts, { zone: 'local' }).toFormat('MMM d, yyyy t');
};

export const formatSyncTime = (ts: number) => {
  return DateTime.fromSeconds(ts, { zone: 'local' }).toFormat('t');
};

export const isToday = (ts: number): boolean => {
  const isSameDay = DateTime.fromSeconds(ts, { zone: 'local' }).hasSame(
    DateTime.local(),
    'day'
  );
  return isSameDay && ts > DateTime.local().toSeconds();
};

export const isUpcoming = (ts: number): boolean => {
  const isSameDay = DateTime.fromSeconds(ts, { zone: 'local' }).hasSame(
    DateTime.local(),
    'day'
  );
  return !isSameDay && ts > DateTime.local().toSeconds();
};

export const isPast = (ts: number): boolean => {
  return ts < DateTime.local().toSeconds();
};

export const isTodayFromString = (date: string): boolean => {
  return DateTime.local().hasSame(DateTime.fromISO(date), 'day');
};

export const getFormattedScheduleDate = (gameStartTs: number): string => {
  const { date: gameDate, time: gameTime } =
    formatScheduleCardDates(gameStartTs);
  if (isToday(gameStartTs)) {
    return `Today @ ${gameTime}`;
  }
  return `${gameDate} @ ${gameTime}`;
};

export const findNextGame = (
  games: TeamGameResponse[]
): TeamGameResponse | null => {
  const now = DateTime.local();
  const notStarted = games.filter((g) => !g.isEnded);
  if (notStarted.length === 1) {
    return notStarted[0];
  }
  return games.reduce<TeamGameResponse | null>((closestGame, game) => {
    const gameDate = DateTime.fromSeconds(game.scheduleTsStart, {
      zone: 'local',
    });

    const diffInMinutes = gameDate.diff(now, 'minutes').toObject().minutes;

    if (diffInMinutes !== undefined && diffInMinutes < 0) {
      return closestGame;
    }

    if (
      !closestGame ||
      (game.scoreTsStart === 0.0 &&
        diffInMinutes !== undefined &&
        gameDate.diff(now, 'minutes').toObject().minutes! <
          DateTime.fromSeconds(closestGame.scheduleTsStart, { zone: 'local' })
            .diff(now, 'minutes')
            .toObject().minutes!)
    ) {
      return game;
    }
    return closestGame;
  }, null);
};

export const calculateTs = (date: string, time: string): number => {
  // Ensure time is properly formatted (e.g., pad single-digit hours)
  const formattedTime = time.padStart(5, '0'); // Ensures "4:00" becomes "04:00"
  // const combinedDateTime = `${date}T${formattedTime}:00`;
  const dateTimeObject = DateTime.fromISO(`${date}T${formattedTime}:00`, {
    zone: 'local',
  });
  return dateTimeObject.toSeconds();
};

export const isWithinHours = (ts: number, diff: number = 12): boolean => {
  const now = DateTime.local();
  const timestamp = DateTime.fromSeconds(ts, { zone: 'local' });

  // Calculate the difference in hours
  const diffInHours = Math.abs(timestamp.diff(now, 'hours').hours);

  // Check if the difference is within 12 hours
  return diffInHours <= diff;
};

export const formatDateTime = (
  ts: number,
  formatDate = 'MMM dd',
  formatTime = 't'
) => {
  const dateTimeLocal = DateTime.fromSeconds(ts, {
    zone: 'local',
  });
  const date = dateTimeLocal.toFormat(formatDate);
  const time = dateTimeLocal.toFormat(formatTime);
  return { date, time };
};
